import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { format } from 'date-fns'

export interface ChartPoint {
  x: number
  y: number
}

interface LineChartProps {
  data: ChartPoint[]
  height?: number
  accentColor?: string
  isLoading?: boolean
  formatXAxis?: (v: number) => string
  formatTooltipX?: (v: number) => string
  formatTooltipY?: (v: number) => string
  minTickGap?: number
  showYAxis?: boolean
  formatYAxis?: (v: number) => string
}

function catmullPath(pts: Array<{ x: number, y: number }>, tension = 20): string {
  if (pts.length === 0) return ''
  if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`
  let d = `M ${pts[0].x.toFixed(2)} ${pts[0].y.toFixed(2)}`
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)]
    const p1 = pts[i]
    const p2 = pts[i + 1]
    const p3 = pts[Math.min(pts.length - 1, i + 2)]
    const cp1x = p1.x + (p2.x - p0.x) / tension
    const cp1y = p1.y + (p2.y - p0.y) / tension
    const cp2x = p2.x - (p3.x - p1.x) / tension
    const cp2y = p2.y - (p3.y - p1.y) / tension
    d += ` C ${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p2.x.toFixed(2)},${p2.y.toFixed(2)}`
  }
  return d
}

const LineChart = ({
  data,
  height = 130,
  accentColor = '#0f766e',
  isLoading = false,
  formatXAxis,
  formatTooltipX,
  formatTooltipY,
  minTickGap = 60,
  showYAxis = false,
  formatYAxis
}: LineChartProps): React.ReactElement => {
  const uid = useId()
  const containerRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(600)
  const [hoverIdx, setHoverIdx] = useState<number | null>(null)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const measure = (): void => setWidth(el.getBoundingClientRect().width)
    measure()
    const obs = new ResizeObserver(measure)
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  const pad = useMemo(() => ({
    top: 12,
    right: 26,
    bottom: 28,
    left: showYAxis ? 58 : 26
  }), [showYAxis])

  const plotW = width - pad.left - pad.right
  const plotH = height - pad.top - pad.bottom
  const hasData = data.some(p => p.y > 0)

  const { minX, maxX, minY, maxY } = useMemo(() => {
    if (data.length === 0) return { minX: 0, maxX: 1, minY: 0, maxY: 1 }
    const xs = data.map(p => p.x)
    const ys = data.map(p => p.y)
    const rawMinY = Math.min(...ys)
    const rawMaxY = Math.max(...ys)
    const yRange = rawMaxY - rawMinY || 1
    return {
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
      minY: rawMinY - yRange * 0.08,
      maxY: rawMaxY + yRange * 0.05
    }
  }, [data])

  const toSvg = useCallback((p: ChartPoint) => ({
    x: pad.left + ((p.x - minX) / (maxX - minX || 1)) * plotW,
    y: pad.top + (1 - (p.y - minY) / (maxY - minY)) * plotH
  }), [minX, maxX, plotW, minY, maxY, plotH, pad])

  const svgPts = useMemo(() => data.map(toSvg), [data, toSvg])
  const linePath = useMemo(() => catmullPath(svgPts), [svgPts])

  const fillPath = useMemo(() => {
    if (svgPts.length < 2) return ''
    const baseline = pad.top + plotH
    const last = svgPts[svgPts.length - 1]
    const first = svgPts[0]
    return `${linePath} L ${last.x.toFixed(2)},${baseline.toFixed(2)} L ${first.x.toFixed(2)},${baseline.toFixed(2)} Z`
  }, [linePath, svgPts, plotH, pad.top])

  const xTicks = useMemo(() => {
    if (data.length === 0) return []
    const result: number[] = []
    let lastX = -Infinity
    let lastLabel = ''
    svgPts.forEach((p, i) => {
      if (p.x - lastX >= minTickGap) {
        const label = formatXAxis ? formatXAxis(data[i].x) : format(new Date(data[i].x), 'MMM d')
        if (label !== lastLabel) {
          result.push(i)
          lastX = p.x
          lastLabel = label
        }
      }
    })
    return result
  }, [svgPts, minTickGap, data, formatXAxis])

  const yTicks = useMemo(() => {
    if (!showYAxis || !hasData) return []
    return Array.from({ length: 4 }, (_, i) => {
      const value = minY + ((maxY - minY) * i) / 3
      const svgY = pad.top + (1 - (value - minY) / (maxY - minY)) * plotH
      return { value, svgY }
    })
  }, [showYAxis, hasData, minY, maxY, plotH, pad.top])

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (svgPts.length === 0) return
    const rect = e.currentTarget.getBoundingClientRect()
    const mx = e.clientX - rect.left
    let closest = 0
    let minDist = Infinity
    svgPts.forEach((p, i) => {
      const d = Math.abs(p.x - mx)
      if (d < minDist) { minDist = d; closest = i }
    })
    setHoverIdx(closest)
  }, [svgPts])

  const gradId = `${uid}-fill`
  const clipId = `${uid}-clip`
  const dotPatId = `${uid}-dots`
  const dotMaskId = `${uid}-dotmask`
  const skelDotPatId = `${uid}-skel-dots`
  const revealClipId = `${uid}-reveal`

  const hoverSvgPt = hoverIdx !== null ? svgPts[hoverIdx] : null
  const hoverDataPt = hoverIdx !== null ? data[hoverIdx] : null

  const tooltipLeft = hoverSvgPt
    ? Math.min(width - 130, Math.max(4, hoverSvgPt.x - 58))
    : 0
  const tooltipTop = hoverSvgPt
    ? Math.max(4, hoverSvgPt.y - 64)
    : 0

  const baseline = pad.top + plotH

  const skeletonSvgPts = useMemo(() => (
    [
      { xf: 0, yf: 0.58 },
      { xf: 0.05, yf: 0.78 },
      { xf: 0.10, yf: 0.22 },
      { xf: 0.16, yf: 0.62 },
      { xf: 0.21, yf: 0.12 },
      { xf: 0.27, yf: 0.55 },
      { xf: 0.32, yf: 0.08 },
      { xf: 0.38, yf: 0.48 },
      { xf: 0.43, yf: 0.85 },
      { xf: 0.49, yf: 0.30 },
      { xf: 0.55, yf: 0.70 },
      { xf: 0.60, yf: 0.18 },
      { xf: 0.66, yf: 0.58 },
      { xf: 0.71, yf: 0.05 },
      { xf: 0.77, yf: 0.42 },
      { xf: 0.82, yf: 0.15 },
      { xf: 0.88, yf: 0.52 },
      { xf: 0.93, yf: 0.10 },
      { xf: 1.0, yf: 0.28 }
    ].map(({ xf, yf }) => ({ x: pad.left + xf * plotW, y: pad.top + yf * plotH }))
  ), [pad, plotW, plotH])

  const skeletonLinePath = useMemo(() => catmullPath(skeletonSvgPts), [skeletonSvgPts])

  const skeletonFillPath = useMemo(() => {
    if (skeletonSvgPts.length < 2) return ''
    const baseY = pad.top + plotH
    const last = skeletonSvgPts[skeletonSvgPts.length - 1]
    const first = skeletonSvgPts[0]
    return `${skeletonLinePath} L ${last.x.toFixed(2)},${baseY.toFixed(2)} L ${first.x.toFixed(2)},${baseY.toFixed(2)} Z`
  }, [skeletonLinePath, skeletonSvgPts, pad, plotH])

  return (
    <div ref={containerRef} style={{ position: 'relative', height }}>
      <svg
        width="100%"
        height={height}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoverIdx(null)}
        style={{ display: 'block', overflow: 'visible' }}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="white" stopOpacity="1" />
            <stop offset="40%" stopColor="white" stopOpacity="0.85" />
            <stop offset="80%" stopColor="white" stopOpacity="0.1" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </linearGradient>
          <mask id={dotMaskId}>
            <rect x={pad.left} y={pad.top} width={plotW} height={plotH} fill={`url(#${gradId})`} />
          </mask>
          <pattern id={dotPatId} x="0" y="0" width="5" height="5" patternUnits="userSpaceOnUse">
            <circle cx="2.5" cy="2.5" r="1.2" fill={accentColor} fillOpacity={hasData ? 0.8 : 0.1} />
          </pattern>
          <pattern id={skelDotPatId} x="0" y="0" width="5" height="5" patternUnits="userSpaceOnUse">
            <circle cx="2.5" cy="2.5" r="1.2" fill="var(--chart-axis-ink, #a8b0c0)" fillOpacity={0.8} />
          </pattern>
          <clipPath id={clipId}>
            <rect x={pad.left} y={pad.top} width={plotW} height={plotH} />
          </clipPath>
          <clipPath id={revealClipId}>
            <rect
              x={pad.left}
              y={pad.top - 6}
              width={isLoading ? 0 : plotW + 6}
              height={plotH + 12}
              style={{ transition: 'width 650ms cubic-bezier(0.22, 1, 0.36, 1)' }}
            />
          </clipPath>
        </defs>

        {/* Thin baseline */}
        <line
          x1={pad.left}
          y1={baseline}
          x2={pad.left + plotW}
          y2={baseline}
          stroke="var(--chart-baseline, #e8ecf2)"
          strokeWidth={1}
        />

        {/* Real chart */}
        <g style={{ opacity: isLoading ? 0 : 1, transition: 'opacity 400ms ease 100ms', pointerEvents: isLoading ? 'none' : 'auto' }}>
          <g clipPath={`url(#${revealClipId})`}>
            {/* Dotted stipple fill */}
            {hasData && fillPath && (
              <path d={fillPath} fill={`url(#${dotPatId})`} mask={`url(#${dotMaskId})`} clipPath={`url(#${clipId})`} />
            )}

            {/* Main line */}
            {linePath && (
              <path
                d={linePath}
                fill="none"
                stroke={accentColor}
                strokeWidth={2}
                strokeOpacity={hasData ? 1 : 0.28}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}

            {/* End-of-line dot */}
            {hasData && svgPts.length > 0 && (
              <circle
                cx={svgPts[svgPts.length - 1].x}
                cy={svgPts[svgPts.length - 1].y}
                r={4}
                fill={accentColor}
                stroke="var(--surface, #fff)"
                strokeWidth={2}
              />
            )}
          </g>

          {/* Hover cursor */}
          {hoverSvgPt && (
            <line
              x1={hoverSvgPt.x}
              y1={pad.top}
              x2={hoverSvgPt.x}
              y2={baseline}
              stroke="var(--chart-cursor, #d0d6e2)"
              strokeWidth={1}
            />
          )}

          {/* Hover dot */}
          {hoverSvgPt && (
            <circle
              cx={hoverSvgPt.x}
              cy={hoverSvgPt.y}
              r={4}
              fill={accentColor}
              stroke="var(--surface, #fff)"
              strokeWidth={2}
            />
          )}

          {/* X-axis labels */}
          {xTicks.map(i => (
            <text
              key={i}
              x={svgPts[i]?.x ?? 0}
              y={height - 7}
              textAnchor="middle"
              fontSize={9.5}
              fill="var(--chart-axis-ink, #a8b0c0)"
              style={{ fontFamily: 'var(--font-mono)' }}
            >
              {formatXAxis ? formatXAxis(data[i].x) : format(new Date(data[i].x), 'MMM d')}
            </text>
          ))}

          {/* Y-axis labels */}
          {showYAxis && yTicks.map(({ value, svgY }, i) => (
            <text
              key={i}
              x={pad.left - 8}
              y={svgY}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize={9.5}
              fill="var(--chart-axis-ink, #a8b0c0)"
              style={{ fontFamily: 'var(--font-mono)' }}
            >
              {formatYAxis ? formatYAxis(value) : value.toFixed(0)}
            </text>
          ))}
        </g>

        {/* Skeleton */}
        <g style={{ opacity: isLoading ? 1 : 0, transition: 'opacity 350ms ease', pointerEvents: 'none' }}>
          <g>
            {isLoading && (
              <animate attributeName="opacity" values="0.3;0.65;0.3" dur="1.5s" repeatCount="indefinite" />
            )}
            <path d={skeletonFillPath} fill={`url(#${skelDotPatId})`} mask={`url(#${dotMaskId})`} clipPath={`url(#${clipId})`} />
            <path d={skeletonLinePath} fill="none" stroke="var(--chart-axis-ink, #a8b0c0)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
            <circle cx={skeletonSvgPts[skeletonSvgPts.length - 1].x} cy={skeletonSvgPts[skeletonSvgPts.length - 1].y} r={4} fill="var(--chart-axis-ink, #a8b0c0)" stroke="var(--surface, #fff)" strokeWidth={2} />
            {[0.12, 0.37, 0.62, 0.87].map((f, i) => (
              <rect key={i} x={pad.left + f * plotW - 14} y={height - 16} width={28} height={8} rx={3} fill="var(--surface-pressed, #d1d5db)" />
            ))}
            {showYAxis && [0.1, 0.38, 0.65, 0.92].map((f, i) => (
              <rect key={i} x={pad.left - 46} y={pad.top + f * plotH - 4} width={38} height={8} rx={3} fill="var(--surface-pressed, #d1d5db)" />
            ))}
          </g>
        </g>
      </svg>

      {/* Tooltip */}
      {hoverDataPt && hoverSvgPt && !isLoading && (
        <div style={{
          position: 'absolute',
          top: tooltipTop,
          left: tooltipLeft,
          background: 'var(--tooltip-bg, #111520)',
          border: '1px solid var(--tooltip-border, rgba(255,255,255,0.08))',
          borderRadius: 7,
          padding: '8px 12px',
          pointerEvents: 'none',
          zIndex: 10,
          boxShadow: '0 8px 28px rgba(0,0,0,0.38), 0 2px 6px rgba(0,0,0,0.18)',
          minWidth: 112
        }}>
          <div style={{ color: 'var(--tooltip-meta, #5c6577)', fontSize: 10.5, letterSpacing: '0.02em', marginBottom: 4 }}>
            {formatTooltipX
              ? formatTooltipX(hoverDataPt.x)
              : format(new Date(hoverDataPt.x), 'MMM d, yyyy')}
          </div>
          <div style={{
            color: 'var(--tooltip-ink, #f0f4ff)',
            fontSize: 13.5,
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '-0.01em'
          }}>
            {formatTooltipY
              ? formatTooltipY(hoverDataPt.y)
              : hoverDataPt.y.toLocaleString()}
          </div>
        </div>
      )}

    </div>
  )
}

export default LineChart